import { lazy, Suspense } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { ProjectLayout } from "../components/layout/ProjectLayout";
import { Skeleton } from "../components/ui";
import { ProtectedRoute } from "../features/auth/ProtectedRoute";
const Login = lazy(() => import("../features/auth/LoginPage"));
const AccessGateway = lazy(() => import("../features/auth/AccessGatewayPage"));
const Dashboard = lazy(() => import("../features/dashboard/DashboardPage"));
const Leads = lazy(() => import("../features/crm/LeadsPage"));
const Clients = lazy(() => import("../features/clients/ClientsPage"));
const ClientDetail = lazy(() => import("../features/clients/ClientDetailPage"));
const Opportunity = lazy(
  () => import("../features/opportunities/OpportunityPage"),
);
const Measurements = lazy(
  () => import("../features/measurements/MeasurementsPage"),
);
const MeasurementDetail = lazy(
  () => import("../features/measurements/MeasurementDetailPage"),
);
const AiStudio = lazy(() => import("../features/ai-studio/AiStudioPage"));
const Quotes = lazy(() => import("../features/quotes/QuotesPage"));
const QuoteEditor = lazy(() => import("../features/quotes/QuoteEditorPage"));
const QuoteDetail = lazy(() => import("../features/quotes/QuoteDetailPage"));
const Projects = lazy(() => import("../features/projects/ProjectsPage"));
const ProjectSummary = lazy(
  () => import("../features/projects/ProjectSummaryPage"),
);
const ProjectOperations = lazy(
  () => import("../features/projects/ProjectOperationsPage"),
);
const Finance = lazy(() => import("../features/finance/FinancePage"));
const Portal = lazy(() => import("../features/client-portal/PortalPage"));
const ClientAccess = lazy(
  () => import("../features/client-portal/ClientAccessPage"),
);
const NotFound = lazy(() => import("../features/misc/NotFoundPage"));
const CatalogPage = lazy(() =>
  import("../features/management/ManagementPages").then((module) => ({
    default: module.CatalogPage,
  })),
);
const ReportsPage = lazy(() =>
  import("../features/management/ManagementPages").then((module) => ({
    default: module.ReportsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("../features/management/ManagementPages").then((module) => ({
    default: module.SettingsPage,
  })),
);
const loading = (
  <div className="route-loading">
    <Skeleton className="skeleton-title" />
    <div className="skeleton-grid">
      <Skeleton />
      <Skeleton />
      <Skeleton />
      <Skeleton />
    </div>
    <Skeleton className="skeleton-panel" />
  </div>
);
const lazyPage = (element: React.ReactNode) => (
  <Suspense fallback={loading}>{element}</Suspense>
);
export const router = createBrowserRouter([
  { path: "/", element: lazyPage(<AccessGateway />) },
  { path: "/login", element: lazyPage(<Login />) },
  { path: "/portal", element: lazyPage(<ClientAccess />) },
  { path: "/portal/:token", element: lazyPage(<Portal />) },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "dashboard", element: lazyPage(<Dashboard />) },
          { path: "comercial/leads", element: lazyPage(<Leads />) },
          { path: "clientes", element: lazyPage(<Clients />) },
          { path: "clientes/:clientId", element: lazyPage(<ClientDetail />) },
          {
            path: "oportunidades/:opportunityId",
            element: lazyPage(<Opportunity />),
          },
          { path: "medicoes", element: lazyPage(<Measurements />) },
          {
            path: "medicoes/:measurementId",
            element: lazyPage(<MeasurementDetail />),
          },
          {
            path: "ai-studio",
            element: <Navigate to="/ai-studio/opp-1" replace />,
          },
          { path: "ai-studio/:opportunityId", element: lazyPage(<AiStudio />) },
          { path: "orcamentos", element: lazyPage(<Quotes />) },
          { path: "orcamentos/novo", element: lazyPage(<QuoteEditor />) },
          { path: "orcamentos/:quoteId", element: lazyPage(<QuoteDetail />) },
          {
            path: "orcamentos/:quoteId/editar",
            element: lazyPage(<QuoteEditor />),
          },
          { path: "obras", element: lazyPage(<Projects />) },
          {
            path: "obras/:projectId",
            element: <ProjectLayout />,
            children: [
              { index: true, element: <Navigate to="resumo" replace /> },
              { path: "resumo", element: lazyPage(<ProjectSummary />) },
              { path: "cronograma", element: lazyPage(<ProjectOperations />) },
              { path: "atividades", element: lazyPage(<ProjectOperations />) },
              { path: "diario", element: lazyPage(<ProjectOperations />) },
              { path: "fotos", element: lazyPage(<ProjectOperations />) },
              { path: "documentos", element: lazyPage(<ProjectOperations />) },
              { path: "equipe", element: lazyPage(<ProjectOperations />) },
              { path: "historico", element: lazyPage(<ProjectOperations />) },
              { path: "financeiro", element: lazyPage(<Finance />) },
            ],
          },
          { path: "financeiro", element: lazyPage(<Finance />) },
          { path: "financeiro/receber", element: lazyPage(<Finance />) },
          { path: "financeiro/pagar", element: lazyPage(<Finance />) },
          { path: "financeiro/despesas", element: lazyPage(<Finance />) },
          { path: "financeiro/fluxo-caixa", element: lazyPage(<Finance />) },
          { path: "cadastros/servicos", element: lazyPage(<CatalogPage />) },
          { path: "cadastros/materiais", element: lazyPage(<CatalogPage />) },
          {
            path: "cadastros/fornecedores",
            element: lazyPage(<CatalogPage />),
          },
          { path: "cadastros/equipe", element: lazyPage(<CatalogPage />) },
          { path: "relatorios", element: lazyPage(<ReportsPage />) },
          { path: "configuracoes", element: lazyPage(<SettingsPage />) },
          {
            path: "configuracoes/empresa",
            element: lazyPage(<SettingsPage />),
          },
          {
            path: "configuracoes/usuarios",
            element: lazyPage(<SettingsPage />),
          },
        ],
      },
    ],
  },
  { path: "*", element: lazyPage(<NotFound />) },
]);
